import { useMemo } from 'react';
import type { DataTableMode } from '@odo/shared-ui';
import type { IteroOrder } from '../../../../services/iteroBrowse';
import { FIRST_PAGE } from './browsePaging';
import { BrowseBody } from './BrowseBody';
import { tableRows, visiblePaneKey } from './paneVisibility';
import { IteroOrdersTable } from './IteroOrdersTable';
import { OrderView } from './order-view/OrderView';
import { PaneLayout } from './pane/PaneLayout';
import { PaneRestoreState } from './pane/PaneRestoreState';
import { identityFromOrder } from './pane/paneIdentity';
import { TableLoadMoreError } from './TableStates';
import type { IteroTabNav } from './useIteroBrowserNavigation';
import { useOrderSelection, type OrderSelection } from './useOrderSelection';
import type { OrdersBrowse } from './useOrdersBrowse';
import { useSelectionPin } from './useSelectionPin';

const orderKey = (order: IteroOrder) => order.rxId;

interface OrdersListProps {
    browse: OrdersBrowse;
    rows: readonly IteroOrder[];
    selectedRxId: string | null;
    onModeChange: (mode: DataTableMode) => void;
    onSelect: (order: IteroOrder) => void;
}

function OrdersList({ browse, rows, selectedRxId, onModeChange, onSelect }: OrdersListProps) {
    return (
        <>
            <IteroOrdersTable
                key={browse.mode}
                rows={rows}
                isLoading={browse.isLoading}
                isUpdating={browse.isUpdating}
                errorMessage={browse.errorMessage}
                hasFilters={browse.showClear}
                selectedRxId={selectedRxId}
                mode={browse.mode}
                onModeChange={onModeChange}
                pagination={browse.pagination}
                hasMore={browse.hasMore && browse.loadMoreError === null}
                isLoadingMore={browse.isLoadingMore}
                onLoadMore={browse.loadMore}
                onSelect={onSelect}
                onClearFilters={browse.clearFilters}
                onRetry={browse.retry}
            />
            {browse.loadMoreError && (
                <TableLoadMoreError message={browse.loadMoreError} onRetry={browse.loadMore} />
            )}
        </>
    );
}

function OrderDetails({ selection, today }: { selection: OrderSelection; today: string }) {
    const { order } = selection;
    if (order.kind !== 'ready') return <PaneRestoreState restored={order} label="order" />;
    return (
        <PaneLayout
            identity={identityFromOrder(order.value)}
            today={today}
            viewKey={order.value.rxId}
            slide="none"
        >
            <OrderView
                order={order.value}
                tab={selection.orderTab}
                onTabChange={selection.setOrderTab}
            />
        </PaneLayout>
    );
}

interface OrdersPanelProps {
    browse: OrdersBrowse;
    nav: IteroTabNav;
    today: string;
    onModeChange: (mode: DataTableMode) => void;
}

export function OrdersPanel({ browse, nav, today, onModeChange }: OrdersPanelProps) {
    const rows = tableRows(browse.orders, browse.errorMessage);
    const selection = useOrderSelection(nav, rows);
    const { selectedRxId } = selection;
    // Memoized so typing in the search box doesn't re-render the details pane.
    const details = useMemo(
        () => selectedRxId !== null && <OrderDetails selection={selection} today={today} />,
        [selectedRxId, selection, today]
    );
    const page = browse.pagination?.page ?? FIRST_PAGE;
    const isPinned = useSelectionPin(selectedRxId, `${browse.listKey}:${page}`);
    const openKey = visiblePaneKey(selectedRxId, rows, orderKey, isPinned);

    return (
        <BrowseBody
            detailsLabel="Order details"
            details={details}
            openKey={openKey}
            onCloseDetails={selection.close}
            list={
                <OrdersList
                    browse={browse}
                    rows={rows}
                    selectedRxId={selectedRxId}
                    onModeChange={onModeChange}
                    onSelect={selection.select}
                />
            }
        />
    );
}
